import { formatISO, parse, isValid, format } from 'date-fns';
import { DEFAULT_DATE_FORMAT, WEB_APP_URL } from '@Consts/constants';
import { DeviceType } from '@Types/onboarding.types';

export const checkIsFromLiliApp = (): boolean => {
  const origins = ['Lili', 'liliapp'] as const;
  let status = false;
  origins.map((origin) => {
    if (navigator.userAgent.toLowerCase().includes(origin.toLowerCase())) {
      status = true;
      return;
    }
  });
  return status;
};

export const checkDeviceType = () => {
  let type = DeviceType.UNKNOWN;
  if (!!navigator.userAgent && /iPad|iPhone|iPod|MacIntel/.test(navigator.userAgent)) {
    type = DeviceType.IOS;
  } else if (!!navigator.userAgent && /android|Android/i.test(navigator.userAgent)) {
    type = DeviceType.ANDROID;
  }
  return type;
};

/**
 * Extracts the Lili app version from the user agent string.
 * Expected formats:
 *   - iOS: "LiliApp iOS Lili/26.1.3 iOS/26.2 (iPhone13,4) Safari/605.1.15"
 *   - Android: "Lili Dev/26.2.1 Android/15 (SM-A155F) ..."
 * Returns null if version cannot be extracted.
 */
export const getLiliAppVersion = () => {
  const userAgent = navigator.userAgent;
  const parts = userAgent.split('/');
  if (parts.length < 2) return null;
  const version = parts[1].split(' ')[0];
  return /^\d+\.\d+\.\d+$/.test(version) ? version : null;
};

/**
 * Checks if the current Lili app version is at least the minimum required version.
 */
export const isLiliAppVersionAtLeast = (minVersion: string) => {
  const currentVersion = getLiliAppVersion();
  if (!currentVersion) return false;

  const current = currentVersion.split('.').map(Number);
  const minimum = minVersion.split('.').map(Number);

  for (let i = 0; i < Math.max(current.length, minimum.length); i++) {
    const curr = current[i] || 0;
    const min = minimum[i] || 0;
    if (curr > min) return true;
    if (curr < min) return false;
  }
  return true; // versions are equal
};

export const getQueryParamsObject = () => {
  const search = window.location.search;
  const params = new URLSearchParams(search);
  const entries = params.entries();
  return Object.fromEntries(entries);
};

export const isObjectEmpty = (obj: Record<string, unknown>): boolean => {
  return Object.keys(obj).length === 0;
};

export const getCookiesList = () => {
  return document.cookie.split(';').map((c) => c.trim());
};
export const triggerBlurForAllFields = () => {
  const formElements = document.querySelectorAll('input, select, textarea');
  formElements.forEach((element) => {
    element.dispatchEvent(new Event('blur'));
  });
};

export const getParsedDateAsISO = (dateStr: string, dateFormat: string = DEFAULT_DATE_FORMAT) => {
  const parsedDate = parse(dateStr, dateFormat, new Date());
  return formatISO(parsedDate);
};

export const getIsDateValid = (dateStr: string, dateFormat: string = DEFAULT_DATE_FORMAT) => {
  let isDateValid = false;
  try {
    const parsedDate = parse(dateStr, dateFormat, new Date());
    isDateValid = isValid(parsedDate);
  } catch {
    isDateValid = false;
  }
  return isDateValid;
};

export const toKebabCase = (str: string) => {
  return (
    str
      // Insert a hyphen before each uppercase letter and convert the letter to lowercase
      .replace(/([a-z])([A-Z])/g, '$1-$2')
      // Convert underscores, spaces, and multiple hyphens to a single hyphen
      .replace(/[_\s]+/g, '-')
      // Convert the entire string to lowercase
      .toLowerCase()
  );
};

export const normalizeInputSpacesOnChange = (str: string) => {
  return str.replace(/\s+/g, ' ').trimStart();
};

export const removeAllSpaces = (str: string) => {
  return str.replace(/\s+/g, '').trim();
};

export const normalizeNationalIdOnChange = (str: string) => {
  return str.replace(/[^a-zA-Z0-9\s/.-]/g, ' ').trimStart();
};

export const normalizeInputSpacesOnBlur = (str: string) => {
  return str.trimEnd();
};

export const normalizeSsnInput = (str: string) => {
  return str.replace(/-/g, '');
};

export const normalizeBirthDateInput = (str: string) => {
  if (str.includes('T')) {
    return str.split('T')[0];
  }
  return str;
};

export const safeParseDate = (dateStr: string, scheme: string) => {
  if (!dateStr) return '';
  const safeDate = parse(normalizeBirthDateInput(dateStr), 'yyyy-MM-dd', new Date());
  if (!isValid(safeDate)) return '';
  return format(safeDate, scheme);
};

export const getUserFirstName = (fullName: string) => {
  const parts = fullName.split(' ');
  return parts.length > 0 ? parts[0] : '';
};

export const convertToTimeFormat = (num: number) => {
  const hours = Math.floor(num / 60);
  const minutes = num % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

export const debounce = function <T extends (...args: unknown[]) => unknown>(func: T, delay = 500) {
  let timeoutId: ReturnType<typeof setTimeout>;
  return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
};

export const throttle = function <T extends (...args: unknown[]) => unknown>(func: T, delay = 500) {
  let lastCall = 0;
  return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
    const now = Date.now();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return func.apply(this, args);
  };
};

export const redirectToWebApp = (queryParams: Record<string, string> | null = null) => {
  const url = new URL(window.location.href);
  const searchParams = new URLSearchParams(url.search);
  if (queryParams) {
    Object.keys(queryParams).forEach((key) => {
      searchParams.set(key, queryParams[key]);
    });
  }

  const newUrl = new URL(WEB_APP_URL);
  newUrl.search = searchParams.toString();
  window.location.replace(newUrl);
};
